import {
  Component,
  Input,
  InputSignal,
  WritableSignal,
  input,
  signal,
  HostBinding,
  inject,
} from '@angular/core';
import { BaseWidget } from '../base-widget/base-widget';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { RatingInputComponent } from '../rating-input/rating-input.component';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ReviewType } from '@app/core/enums/review-type.enum';
import { MediaUploaderComponent } from '../media-uploader/media-uploader.component';
import { GlobalValidator } from '@app/core';
import {
  ActionEnum,
  ActionRequestDto,
} from '@app/core/dto/abstract-action.dto';
import { NavigationOptions } from '@app/core/enums/navigation-options.enum';

@Component({
  selector: 'reviewer',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RatingInputComponent,
    MediaUploaderComponent,
  ],
  templateUrl: './reviewer.component.html',
})
export class ReviewerComponent extends BaseWidget {
  reviewerData: InputSignal<any> = input.required();

  @HostBinding('class') @Input() ngClass: any;
  @HostBinding('style') @Input() ngStyle: any;
  @Input() actionEvent: (event: any) => void;
  @Input() slot: any;
  data: WritableSignal<any> = signal({});

  private readonly formBuilder: FormBuilder = inject(FormBuilder);

  reviewForm: FormGroup;
  type = signal(ReviewType.INCOMPLETE);

  reviewTypes = ReviewType;
  metaRatingsCount = signal(3);

  constructor() {
    super();
  }

  ngOnInit(): void {
    this.data.set(this.slot?.widget?.data);
    switch (true) {
      case !this.data()?.renderableComponents?.review: {
        this.type.set(ReviewType.INCOMPLETE);
        break;
      }
      case !this.data()?.renderableComponents?.review?.meta ||
        Object.keys(this.data()?.renderableComponents?.review?.meta).length <
          3: {
        this.type.set(ReviewType.PARTIALLY_COMPLETED);
        break;
      }
      case !!this.data()?.renderableComponents?.review: {
        this.type.set(ReviewType.COMPLETE);
        break;
      }
      case this.data()?.renderableComponents?.review?.approved == false: {
        this.type.set(ReviewType.REJECTED);
        break;
      }
      case this.data()?.renderableComponents?.review?.approved == true: {
        this.type.set(ReviewType.LIVE);
        break;
      }
    }

    this.reviewForm = this.formBuilder.group({
      reviewRating: [null, [Validators.required]],
      reviewTitle: [null, [Validators.required]],
      reviewContent: [null, [Validators.required]],
      reviewFiles: [
        [],
        GlobalValidator.minArrayLength(
          this.data()?.validations?.media?.minQuantity || 0,
        ),
      ],
      reviewMeta: [{}, [GlobalValidator.minObjectLength(3)]],
      reviewType: [this.type(), []],
    });

    // Remove the conditional clearing of reviewMeta validators
    // if (this.type() === ReviewType.INCOMPLETE) {
    //   this.reviewForm.get('reviewMeta').clearValidators();
    // }

    if (this.data()?.settings) {
      this.reviewForm
        .get('reviewTitle')
        .addValidators(
          Validators.minLength(this.data()?.settings?.min_character_title),
        );
      this.reviewForm
        .get('reviewTitle')
        .addValidators(
          Validators.maxLength(this.data()?.settings?.max_character_title),
        );

      this.reviewForm
        .get('reviewContent')
        .addValidators(
          Validators.minLength(this.data()?.settings?.min_character),
        );
      this.reviewForm
        .get('reviewContent')
        .addValidators(
          Validators.maxLength(this.data()?.settings?.max_character),
        );
    }
  }

  performAction(action: ActionRequestDto) {
    this.actionEvent({
      actionData: {
        ...action,
      },
    });
  }

  onRatingChange(newRating: number, metaId?: string) {
    if (!newRating || typeof newRating != 'number') {
      return;
    }

    if (metaId) {
      this.reviewForm.patchValue({
        reviewMeta: {
          ...this.reviewForm.value.reviewMeta,
          [metaId]: newRating,
        },
      });
    } else {
      this.reviewForm.patchValue({
        reviewRating: newRating,
      });
    }
  }

  progressReview() {
    this.reviewForm
      ?.get('reviewMeta')
      ?.addValidators(GlobalValidator.minObjectLength(3));
    this.reviewForm.patchValue({
      reviewMeta: {},
    });
    this.type.set(ReviewType.PARTIALLY_COMPLETED);
  }

  submitReview(skip = false) {
    if (
      this.type() !== ReviewType.PARTIALLY_COMPLETED &&
      this.reviewForm.invalid
    ) {
      return;
    }

    const reviewFormData = {
      ...this.reviewForm.value,
      sku: this.data()?.renderableComponents?.sku,
    };
    reviewFormData.orderId = this.data()?.renderableComponents?.id;
    if (skip) {
      reviewFormData.reviewMeta = {};
    }
    this.actionEvent({
      actionData: {
        type: this.data()?.reviewFormDialogData?.value?.submitReview?.action
          ?.type,
        params: reviewFormData,
      },
    });
  }

  onUpload(event) {
    this.reviewForm.patchValue({
      reviewFiles: [...this.reviewForm.value.reviewFiles, event.url],
    });
  }

  onDelete(event) {
    this.reviewForm.patchValue({
      reviewFiles: this.reviewForm.value.reviewFiles.filter(
        (file) => file !== event.url,
      ),
    });
  }

  performRedirection(type) {
    if (type === 'home') {
      this.actionEvent({
        actionData: {
          type: ActionEnum.NAVIGATION_ROUTER,
          params: {
            url: '/',
            navigationOption: NavigationOptions.SELF,
          },
        },
      });
    } else {
      this.actionEvent({
        actionData: {
          type: ActionEnum.NAVIGATION_ROUTER,
          params: {
            url: '/user/write-review',
            navigationOption: NavigationOptions.SELF,
          },
        },
      });
    }
  }
}
